import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import IWGenericTable, {
  GenericTableColumn,
} from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWPageHeader, {
  StyledTitle,
} from 'shared/components/thunderbolt/IWPageHeader'
import { getSubmissionHistoryBySubmissionGroupId } from 'shared/loadSchedulingClient'
import styled from 'styled-components'
import { getSubmissionHistoryTableSchemaByMarket } from '../schemas'
import { SubmissionHistory } from '../types'

const StyledWrapper = styled.div`
  max-width: ${(props) => props.theme.dimensions.maxScreenWidth};
  display: flex;
  flex: 1;
  align-items: center;
  align-content: center;
  justify-content: space-between;
`

const LeftHand = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
`

const StyledButton = styled.button`
  cursor: pointer;
  border: none;
  background: none;
  margin-right: 0.5rem;
  color: ${(props) => props.theme.palette.primary[700]};
  font-size: ${(props) => props.theme.typography.fontSizes.lg};
`

const USE_QUERY_KEY = 'submission-history-by-submission-meta-id'

export default function SubmissionHistoryDetailsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { submissionGroupId = '', market: currentMarket = '' } = useParams()
  const [searchParams] = useSearchParams()
  const [columns, setColumns] = useState<GenericTableColumn[]>([])
  const [tableItems, setTableItems] = useState<unknown[]>([])

  const { data: submissionItems, isLoading } = useQuery([USE_QUERY_KEY], () => {
    const queryParams = {
      market: currentMarket,
      brand: searchParams.get('brand'),
      zone: searchParams.get('zone'),
      location: searchParams.get('location'),
    }

    return getSubmissionHistoryBySubmissionGroupId(
      submissionGroupId,
      queryParams,
    )
  })

  const transformErcotSubmissionHistoryData = (data: SubmissionHistory[]) => {
    return data.reduce((memo, submission) => {
      const currentSchedulingTypeTranslation =
        submission.schedulingType === 'PHYSICAL_SCHEDULING'
          ? t('loadScheduling.physicalType')
          : t('loadScheduling.demandType')

      const base = {
        ...submission,
        schedulingType: currentSchedulingTypeTranslation,
        createdAt: DateTime.fromISO(submission.createdAt as string).toISO(),
        updatedAt: DateTime.fromISO(submission.updatedAt as string).toISO(),
      }

      if (submission.schedulingType === 'PHYSICAL_SCHEDULING') {
        if (typeof base.intervals === 'string') {
          base.intervals = JSON.parse(base.intervals)
        }

        base.intervals.forEach(({ interval, load }) => {
          base[interval] = load
        })
        memo.push(base)
      } else {
        submission.intervals.forEach(
          ({ interval, blocks, submitType, curveType }) => {
            const flattened = (blocks || []).map((block) => ({
              ...base,
              interval,
              load: block.load,
              price: block.price,
              block: block.block,
              submitType,
              curveType,
            }))
            memo.push(...flattened)
          },
        )
      }

      return memo
    }, [] as unknown[])
  }

  const tableMapper = (market: string, data: SubmissionHistory[]) => {
    const orderedData = orderBy(data, [
      'market',
      'brand',
      'zone',
      'locationName',
      'location',
      'date',
    ])

    if (market.toUpperCase() === 'ERCOT') {
      return transformErcotSubmissionHistoryData(orderedData)
    }

    return orderedData.map((item) => {
      const submission = { ...item }
      submission.market = market

      submission.createdAt =
        DateTime.fromISO(submission.createdAt as string).toISO() ?? 'N/A'

      submission.schedulingType =
        submission.schedulingType === 'PHYSICAL_SCHEDULING'
          ? t('loadScheduling.physicalType')
          : submission.schedulingType === 'DEMAND_SCHEDULING'
          ? t('loadScheduling.demandType')
          : 'N/A'

      submission.message = submission.wasSuccessful
        ? submission.submissionId
        : !submission.wasSuccessful && submission.submissionLog
        ? submission.submissionLog
        : t('loadScheduling.failed')

      submission.submitStatus = submission.wasSuccessful
        ? t('loadScheduling.success')
        : t('loadScheduling.failed')

      if (typeof submission.intervals === 'string') {
        submission.intervals = JSON.parse(submission.intervals)
      }

      submission.intervals.forEach(({ interval, load }) => {
        submission[interval] = load
      })

      return submission
    })
  }

  useEffect(() => {
    const schedulingType = searchParams.get('schedulingType') ?? undefined
    const currentSchedulingType =
      schedulingType === 'Physical'
        ? 'PHYSICAL_SCHEDULING'
        : schedulingType === 'Demand'
        ? 'DEMAND_SCHEDULING'
        : undefined

    const transformedSubmissions = tableMapper(
      currentMarket,
      submissionItems ?? [],
    )

    const cols = getSubmissionHistoryTableSchemaByMarket(
      currentMarket,
      currentSchedulingType,
    )

    setTableItems(transformedSubmissions)
    setColumns(cols)
  }, [submissionItems])

  return (
    <>
      <IWPageHeader data-testid="page-header">
        <StyledWrapper>
          <LeftHand>
            <StyledButton
              onClick={() => navigate(-1)}
              data-testid="page-header-back-button"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </StyledButton>
            <StyledTitle>
              {t('loadScheduling.submissionHistoryDetails')}
            </StyledTitle>
          </LeftHand>
        </StyledWrapper>
      </IWPageHeader>

      <main>
        {isLoading && <IWLoading />}

        {Boolean(tableItems?.length) && !isLoading && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={tableItems}
            columns={columns}
            showColumnSettings
            initialHiddenColumns={['submissionId']}
          />
        )}
      </main>
    </>
  )
}
